import {
  agentLabel,
  sortAgentsByRelevance,
  sortTeamsByRelevance,
  teamLabel,
} from './teamRelevance'
import type { ChatwootAssignee, ChatwootTeamRef } from './types'

// A mention target is an agent (Chatwoot user) or a team. (REQ-01)
export interface MentionTarget {
  type: 'user' | 'team'
  id: string
  name: string
}

// The active @-trigger: the query typed after `@` plus the index of that `@`.
export interface MentionContext {
  query: string
  // Index of the triggering `@` in the content.
  start: number
}

// Agent names routinely contain spaces ("Matthias Lothes"), so the active query
// may span spaces too — otherwise the picker vanished the moment the operator
// typed the space between first and last name, leaving them unsure the mention
// would register. To keep an abandoned "@" earlier in the note from turning the
// rest of a sentence into a query, we cap the span at this many words; a real
// name never exceeds it, and past the cap the trigger simply closes.
const MAX_MENTION_QUERY_WORDS = 6

// The @-query active at the caret — the `@…` token immediately to the left of the
// cursor (after start or whitespace), or null when no trigger is active. Scanning
// up to the caret (not the whole string) is what lets mentions work mid-note, not
// only at the very end. The query may contain spaces so multi-word names keep
// matching; it stops at a newline or a second `@`, and email addresses like
// "a@b.com" never trigger because the `@` isn't at a word boundary.
export function mentionQueryAt(
  content: string,
  caretIndex: number = content.length
): MentionContext | null {
  const caret = Math.max(0, Math.min(caretIndex, content.length))
  const left = content.slice(0, caret)
  const match = left.match(/(?:^|\s)@([^\n@]*)$/)
  if (!match) {
    return null
  }
  const query = match[1]
  // Beyond a name's worth of words this is an abandoned trigger, not a mention —
  // close the picker so it doesn't stay latched across a whole sentence.
  if (query.split(/\s+/).filter(Boolean).length > MAX_MENTION_QUERY_WORDS) {
    return null
  }
  return { query, start: caret - query.length - 1 }
}

// Agents + teams as mention targets, ranked by relevance: the picker only
// shows the first handful of matches, so the order here decides who an
// operator can reach without typing more. Payroll accounting leads, then
// everyone else alphabetically; teams keep following the agents.
export function mentionTargets(
  agents: ChatwootAssignee[],
  teams: ChatwootTeamRef[]
): MentionTarget[] {
  return [
    ...sortAgentsByRelevance(agents, teams).map((agent) => ({
      type: 'user' as const,
      id: agent.id,
      name: agentLabel(agent),
    })),
    ...sortTeamsByRelevance(teams).map((team) => ({
      type: 'team' as const,
      id: team.id,
      name: teamLabel(team),
    })),
  ]
}

export function filterMentionTargets(
  targets: MentionTarget[],
  query: string
): MentionTarget[] {
  const normalized = query.toLowerCase()
  return targets.filter((target) =>
    target.name.toLowerCase().includes(normalized)
  )
}

// Escape the markdown-significant characters in a mention label so a name
// containing `]` or `\` can't break out of the link text and corrupt the markup.
function escapeMentionLabel(label: string): string {
  return label.replace(/([\\\]])/g, '\\$1')
}

// Chatwoot mention markup for a target: a markdown link whose URL Chatwoot
// parses (`mention://user/<id>`) to notify the mentioned agent/team. The visible
// label is the link text (escaped); the URL keeps the encoded raw name.
function mentionMarkup(target: MentionTarget): string {
  return `[@${escapeMentionLabel(target.name)}](mention://${target.type}/${target.id}/${encodeURIComponent(
    target.name
  )})`
}

// Replace the active "@query" span (from the trigger `@` up to the caret) with a
// plain, human-readable "@Name" token and return the new content plus the caret
// position after it. The textarea is plain text, so the draft stays readable (no
// raw `mention://` markup); we only expand to Chatwoot markup at send time (see
// serializeMentions). A trailing space is added unless one already follows, so a
// mid-note mention doesn't glue onto the next word.
export function insertMention(
  content: string,
  context: MentionContext,
  target: MentionTarget,
  caretIndex: number = content.length
): { content: string; caret: number } {
  const caret = Math.max(context.start, Math.min(caretIndex, content.length))
  const before = content.slice(0, context.start)
  const after = content.slice(caret)
  const token = `@${target.name}${after.startsWith(' ') ? '' : ' '}`
  return {
    content: before + token + after,
    caret: before.length + token.length,
  }
}

// Expand the readable "@Name" tokens in a draft into Chatwoot mention markup,
// matching (case-insensitively) against the known agents/teams. Names are matched
// longest-first so "@Anna Lee" wins over "@Anna", and a token only converts when
// it sits on a word boundary and matches a target — manual "@foo" that isn't an
// agent stays literal. The emitted label uses the directory's canonical casing,
// so "@amina pestek" still resolves to the right person. Already-expanded markup
// (`[@Name](mention://…)`) is left untouched because its "@" is preceded by "[".
export function serializeMentions(
  content: string,
  targets: MentionTarget[]
): string {
  if (targets.length === 0) {
    return content
  }
  const sorted = [...targets].sort((a, b) => b.name.length - a.name.length)
  let result = ''
  let index = 0
  while (index < content.length) {
    const char = content[index]
    const atBoundary = index === 0 || /\s/.test(content[index - 1])
    if (char === '@' && atBoundary) {
      const rest = content.slice(index + 1)
      const target = sorted.find((candidate) => {
        if (
          rest.slice(0, candidate.name.length).toLowerCase() !==
          candidate.name.toLowerCase()
        ) {
          return false
        }
        const after = rest[candidate.name.length]
        return after === undefined || /[\s.,;:!?)]/.test(after)
      })
      if (target) {
        result += mentionMarkup(target)
        index += 1 + target.name.length
        continue
      }
    }
    result += char
    index += 1
  }
  return result
}
